import type { ButtonProps as MantineButtonProps } from '@mantine/core';
import {
  Button as MantineButton,
  createPolymorphicComponent,
} from '@mantine/core';
import clsx from 'clsx';
import { forwardRef, type ReactNode } from 'react';
import { useTheme } from 'styled-components';
import { RIcon, type RIconComponent } from '../remix-icon/remix-icon';
import styles from './button.module.css';

export interface ButtonProps extends MantineButtonProps {
  leftIcon?: RIconComponent;
  rightIcon?: RIconComponent;
  variant?:
    | 'primary'
    | 'primary-light'
    | 'outline'
    | 'white'
    | 'subtle'
    | 'grey'
    | 'text';
}

const InnerButton = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      leftIcon,
      rightIcon,
      leftSection,
      rightSection,
      variant = 'primary',
      children,
      className,
      ...rest
    },
    ref
  ) => {
    const theme = useTheme();

    return (
      <MantineButton
        ref={ref}
        className={clsx(styles.root, className)}
        data-variant={variant}
        leftSection={leftIcon ? <RIcon icon={leftIcon} /> : leftSection}
        rightSection={rightIcon ? <RIcon icon={rightIcon} /> : rightSection}
        data-testid={getDataTestId(children)}
        loaderProps={{
          color:
            variant === 'primary'
              ? theme.palette.neutral['00']
              : theme.palette.neutral[100],
        }}
        {...rest}
      >
        {children}
      </MantineButton>
    );
  }
);

InnerButton.displayName = 'Button';

const Button = createPolymorphicComponent<'button', ButtonProps>(InnerButton);

export default Button;

const getDataTestId = (children?: ReactNode) => {
  if (typeof children === 'string') {
    return children.replaceAll(' ', '_');
  }
  return;
};
